import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';

import { RootState } from '../../store/store';
import { useAppSelector, useAppDispatch } from '../../store/hooks/useTypedSelector';
import { updateCart } from '../../store/slices/cartSlice';
import { product } from '../../types';
import { fetchProduct } from '../../store/slices/productSlice';
import Notification from '../common/Notification';
import './_productdetails.scss';
import ProductImageModal from './ProductImageModal';

const ProductDetails = () => {
  const { id } = useParams();
  const products = useAppSelector((state: RootState) => state.product.filteredProducts);
  const navigate = useNavigate();
  const cart = useAppSelector((state: RootState) => state.cart);
  const [qty, setQty] = useState<number>(1);
  const [showSkeleton, updateSkeleton] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const handleCloseImageModal = () => setOpen(false);
  const [productDetails, setProductDetails] = useState<product>({
    id: 1,
    categoryId: 1,
    imageSrc: '',
    name: '',
    price: '',
    description: '',
  });
  const [productNotFound, setProductNotFound] = useState<boolean>(false);
  const [showMessage, setMessage] = useState<string>('');
  const [isSuccess, setSuccess] = useState<boolean>(false);
  const { state: item } = useLocation();
  const quantityArr = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const dispatch = useAppDispatch();
  const updateItemToCart = (product: product) => {
    if (cart.products.length) {
      const productAlreadyInCart = cart.products.find((prod) => prod.id === productDetails.id);
      if (productAlreadyInCart?.quantity) {
        const totalQuantity = productAlreadyInCart.quantity + qty;
        if (totalQuantity > 10) {
          setMessage('');
          setTimeout(() => {
            setMessage('User can add maximum of 10 item of same products in the cart');
          }, 100);
          setSuccess(false);
          return;
        }
      }
    }
    const productData = { ...product, quantity: qty, productTotal: qty };
    dispatch(updateCart(productData));
    setMessage('');
    setTimeout(() => {
      setMessage('Product/s Added to Cart successfully!');
    }, 100);
    setSuccess(true);
  };
  const handleClose = () => {
    setMessage('');
    setSuccess(false);
  };
  const onImageLoad = () => {
    updateSkeleton(false);
  };
  useEffect(() => {
    if (item === null) {
      dispatch(fetchProduct());
    } else {
      setProductDetails(item);
    }
  }, [id]);
  useEffect(() => {
    if (item === null && products.length && id && !productDetails.imageSrc) {
      const currentProduct = products.find((prod) => prod.id === +id);
      if (currentProduct) {
        setProductDetails(currentProduct);
      } else {
        setProductNotFound(true);
      }
    }
  }, [products]);
  return (
    <div className='container main-container'>
      <ArrowBackIcon
        className='position-absolute'
        style={{ cursor: 'pointer' }}
        onClick={() => navigate(-1)}
        data-testid='svg-back'
      />
      <Paper style={{ padding: '12px', marginLeft: '32px' }}>
        <div className='row'>
          {productDetails.imageSrc && (
            <>
              <div className='col-lg-4'>
                {showSkeleton && (
                  <Skeleton
                    variant='rounded'
                    height={360}
                    width={270}
                    style={{ marginBottom: '-24px' }}
                  />
                )}
                <img
                  src={productDetails.imageSrc}
                  className='cursor-pointer'
                  onLoad={onImageLoad}
                  data-testid='product-image'
                  onClick={() => setOpen(true)}
                />
              </div>
              <div className='col-lg-8'>
                <div className='product_details_text'>
                  <h3>{productDetails.name}</h3>
                  <div className='product_details_price'>
                    ₹ {parseFloat(productDetails.price).toFixed(2)}
                  </div>
                  <p>{productDetails.description}</p>
                  <Stack spacing={2} direction='row' sx={{ mb: 1 }} alignItems='center'>
                    <TextField
                      style={{ width: 150 }}
                      select
                      label='Select Quantity'
                      value={qty}
                      SelectProps={{
                        native: true,
                      }}
                      data-testid='change-quantity'
                      onChange={({ target: { value } }) => {
                        setQty(parseInt(value));
                      }}
                    >
                      {quantityArr.map((quantity) => (
                        <option key={quantity} value={quantity}>
                          {quantity}
                        </option>
                      ))}
                    </TextField>
                    <Button
                      variant='contained'
                      color='success'
                      data-testid='add-to-cart-button'
                      onClick={() => {
                        updateItemToCart(productDetails);
                      }}
                    >
                      Add To Cart
                    </Button>
                  </Stack>
                </div>
              </div>
            </>
          )}
          {productNotFound && (
            <Typography
              gutterBottom
              variant='h5'
              component='div'
              className='d-flex justify-content-center'
            >
              Product Not Found!
            </Typography>
          )}
        </div>
      </Paper>
      {showMessage !== '' && (
        <Notification showMessage={showMessage} handleClose={handleClose} isSuccess={isSuccess} />
      )}
      <ProductImageModal
        open={open}
        handleClose={handleCloseImageModal}
        image={productDetails.imageSrc}
      />
    </div>
  );
};

export default ProductDetails;
